import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import type { MenuTreeNode } from '../types/menu';

interface MenuDeleteConfirmProps {
  open: boolean;
  node: MenuTreeNode | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  /** Error code from API (e.g. MENU-409-002, MENU-403-001) */
  errorCode?: string;
  errorMessage?: string;
}


/**
 * MenuDeleteConfirm — 메뉴 삭제 확인 다이얼로그.
 * - children 있으면 MENU-409-002 에러 표시 + 차단
 * - system_menu=true 이면 MENU-403-001 에러 표시 + 차단
 */
export function MenuDeleteConfirm({
  open,
  node,
  onClose,
  onConfirm,
  isDeleting,
  errorCode,
  errorMessage,
}: MenuDeleteConfirmProps) {
  const { t } = useTranslation();
  if (!node) return null;

  const ERROR_MESSAGES: Record<string, string> = {
    'MENU-409-002': t('menu.error.MENU-409-002'),
    'MENU-403-001': t('menu.error.MENU-403-001'),
    'MENU-404-001': t('menu.error.MENU-404-001'),
  };

  const isBlocked =
    errorCode === 'MENU-409-002' || errorCode === 'MENU-403-001';

  const displayedError = errorCode
    ? (ERROR_MESSAGES[errorCode] ?? errorMessage ?? t('menu.delete.systemMenu'))
    : null;

  const childCount =
    node.children && node.children.length > 0 ? node.children.length : 0;
  const preBlockedByChildren = childCount > 0;
  const preBlockedBySystem = node.systemMenu;

  return (
    <FormDialog
      open={open}
      title={t('menu.delete.title')}
      onClose={onClose}
      onSubmit={isBlocked || preBlockedByChildren || preBlockedBySystem ? onClose : onConfirm}
      submitLabel={
        isBlocked || preBlockedByChildren || preBlockedBySystem
          ? t('menu.tree.orderSave')
          : isDeleting
          ? t('menu.delete.loading')
          : t('menu.delete.confirm')
      }
      cancelLabel={isBlocked || preBlockedByChildren || preBlockedBySystem ? undefined : t('menu.delete.cancel')}
    >
      <div>
        {/* Pre-validation block: children or system menu */}
        {preBlockedBySystem ? (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 8%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {t('menu.delete.systemMenu')}
          </div>
        ) : preBlockedByChildren ? (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-warning) 8%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-warning)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {t('menu.delete.hasChildren', { count: childCount })}
          </div>
        ) : null}

        {/* API error (returned after attempted delete) */}
        {displayedError && !preBlockedBySystem && !preBlockedByChildren && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 8%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {displayedError}
          </div>
        )}

        {!preBlockedBySystem && !preBlockedByChildren && !isBlocked && (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
            {t('menu.delete.description', { nameKo: node.nameKo })}
          </p>
        )}

        {/* Children list if any */}
        {node.children && node.children.length > 0 && (
          <ul
            style={{
              marginTop: 'var(--spacing-3)',
              paddingLeft: 'var(--spacing-4)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {node.children.map((child) => (
              <li key={child.id}>{child.nameKo}</li>
            ))}
          </ul>
        )}
      </div>
    </FormDialog>
  );
}
